/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import moment from 'moment';
import classNames from 'classnames';

import Tooltip from '../Tooltip';
import Pagination from '../Pagination';

import infoIcon from '../../assets/images/my-meetings/info.svg';
import playIcon from '../../assets/images/my-meetings/play.svg';
import copyIcon from '../../assets/images/copyIcon.svg';
import trashIcon from '../../assets/images/nav/trash-bin.svg';

import UserService from '../../services/UserService';
import MeetingService from '../../services/MeetingService';
import { UiContext } from '../../context/UiContext';

const formatDuration = (duration) => {
  if (!duration) {
    return 'N/A';
  }

  const totalSeconds = parseInt(duration, 10);

  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes} min ${seconds} sec`;
};

export default function MeetingHistoryList({ classes, meetingId }) {
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showConfirmActionModal } = useContext(UiContext);

  const { data: meetingVersions } = useQuery(
    ['meetingVersions', meetingId, page],
    () => MeetingService.getMeetingVersions(meetingId, page),
    {
      keepPreviousData: true,
    }
  );

  const { data: user } = useQuery('me', UserService.getMe);

  const isOrganizationOwner = user?.orgRole?.access === 'owner';

  const deleteVersionMutation = useMutation(
    (versionId) => MeetingService.deleteMeetingVersion(meetingId, versionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['meetingVersions', meetingId]);
      },
    }
  );

  const switchPage = (pageNumber) => {
    setPage(pageNumber);
  };

  useEffect(() => {
    if (meetingVersions?.rows) {
      if (meetingVersions.rows.length === 0 && page !== 1) {
        setPage((prevPage) => prevPage - 1);
      }
    }
  }, [meetingVersions?.rows, page]);

  const handleCopyMeeting = (meetingVersion) => {
    showConfirmActionModal({
      title: 'Copy video version',
      message: `This will create a new draft from Version ${meetingVersion.version}. When published, it will become the next version. Continue?`,
      onConfirm: async () => {
        try {
          const meeting = await MeetingService.copyMeeting(meetingVersion.id);
          navigate(`/my-videos/${meeting.id}/edit`);
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const handleDeleteVersion = (meetingVersion) => {
    showConfirmActionModal({
      message: `Are you sure you want to delete Version ${meetingVersion.version}? This action cannot be undone.`,
      onConfirm: () => deleteVersionMutation.mutate(meetingVersion.id),
    });
  };

  // Check if a version can be deleted (not latest, not only one)
  const canDeleteVersion = (meetingVersion) => {
    // Use total count, not current page rows length
    const totalVersions = meetingVersions?.count || 0;
    if (totalVersions <= 1) {
      return false;
    }
    // Versions are sorted by version DESC, so latest is on page 1
    // If we're on page > 1, none of these are the latest
    if (page > 1) {
      return true;
    }
    // On page 1, first row is the latest version
    const latestVersion = meetingVersions?.rows?.[0];
    return latestVersion && meetingVersion.id !== latestVersion.id;
  };

  return (
    <>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>Title</th>
              <th>Version</th>
              <th>Published at</th>
              <th>Created by</th>
              <th>Comments</th>
              <th>Action</th>
            </tr>
          </thead>
          {meetingVersions?.rows && (
            <tbody>
              {meetingVersions?.rows &&
                meetingVersions?.rows.map((meetingVersion) => {
                  const duration = formatDuration(meetingVersion?.duration);

                  return (
                    <tr key={meetingVersion.id}>
                      <td className={classes.title}>{meetingVersion.title}</td>
                      <td className={classes.version}>
                        {meetingVersion.version}
                      </td>
                      <td>
                        {moment(meetingVersion.updatedAt).format('MM/DD/YYYY')}
                      </td>
                      <td>
                        {meetingVersion.Meeting?.userId === user?.id ? (
                          'Me'
                        ) : (
                          <>
                            {meetingVersion?.User.name}
                            <br />
                            {meetingVersion?.User.title}
                          </>
                        )}
                      </td>
                      <td className={classes.comment}>
                        <Tooltip
                          position="top-right"
                          text={meetingVersion.comment}
                        >
                          <span>{meetingVersion.comment}</span>
                        </Tooltip>
                      </td>
                      <td className={classes.buttons}>
                        {/* CSS uses flex-direction: row-reverse, so DOM order is reversed visually */}
                        {/* DOM order: Delete, Copy, Play, Info → Visual order: Info, Play, Copy, Delete */}

                        {isOrganizationOwner && canDeleteVersion(meetingVersion) && (
                          <Tooltip
                            position="top-right"
                            text="Delete version"
                            width={130}
                          >
                            <button
                              className={classNames(
                                classes.button,
                                classes.actionButton
                              )}
                              type="button"
                              onClick={() => handleDeleteVersion(meetingVersion)}
                              disabled={deleteVersionMutation.isLoading}
                            >
                              <img src={trashIcon} alt="Delete" />
                            </button>
                          </Tooltip>
                        )}

                        {isOrganizationOwner && (
                          <Tooltip
                            position="top-right"
                            text="Make a copy"
                            width={130}
                          >
                            <button
                              className={classNames(
                                classes.button,
                                classes.actionButton
                              )}
                              type="button"
                              onClick={() => handleCopyMeeting(meetingVersion)}
                            >
                              <img src={copyIcon} alt="Copy" />
                            </button>
                          </Tooltip>
                        )}

                        <Tooltip position="top-right" text="Play">
                          <button
                            className={classes.actionButton}
                            type="button"
                            onClick={() =>
                              navigate(
                                `/my-videos/${meetingVersion.meetingId}?version=${meetingVersion.version}`
                              )
                            }
                          >
                            <img src={playIcon} alt="Play" />
                          </button>
                        </Tooltip>

                        <Tooltip
                          width={160}
                          position="top-right"
                          text={
                            <>
                              <div
                                className={classes.tooltipContent}
                                style={{ textAlign: 'left' }}
                              >
                                <p>Duration: {duration}</p>

                                {!!meetingVersion.Meeting.MeetingReviews
                                  .length && (
                                  <>
                                    Approved by:
                                    <br />
                                    {meetingVersion.Meeting.MeetingReviews.map(
                                      (review) => (
                                        <>
                                          {review.User
                                            ? `${review.User.name}, ${review.User.title}`
                                            : review.email}
                                          <br />
                                        </>
                                      )
                                    )}
                                  </>
                                )}
                              </div>
                            </>
                          }
                        >
                          <button
                            type="button"
                            className={classes.actionButton}
                          >
                            <img src={infoIcon} alt="Info" />
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          )}
        </table>
      </div>

      {+meetingVersions?.count > 5 && (
        <Pagination
          activePage={page}
          itemsCountPerPage={5}
          totalItemsCount={+meetingVersions?.count || 0}
          pageRangeDisplayed={5}
          onChange={switchPage}
        />
      )}
    </>
  );
}
